import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import {
  showErrorAlert,
  showSuccessAlert,
  showSuccessEditAlert,
} from "../../../components/alerts";
import medicGeekApi from "../../../config/axiosConfig";
import "./../../../styles/FormPro.css";

const ExternalCauseForm = ({ currentForm }) => {
  const [externalCause, setExternalCause] = useState([]);
  const [showForm] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const externalCausePerPage = 10;
  const [selectedExternalCauseLocal, setSelectedExternalCauseLocal] =
    useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Skeleton
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [formulario, setFormulario] = useState({
    nombreExternalCause: "",
    descripcionExternalCause: "",
    estatusExternalCause: "activo",
  });

  const openModal = (externalCause) => {
    setSelectedExternalCauseLocal(externalCause); // Configura el ExternalCause seleccionado para la edición

    if (externalCause) {
      setIsEditing(true); // Establecer como modo edición cuando se abre el modal
      setFormulario({
        nombreExternalCause: externalCause.externalCauseNm,
        descripcionExternalCause: externalCause.externalCauseDesc,
        estatusExternalCause: externalCause.estatus ? "activo" : "inactivo",
      });
    } else {
      setIsEditing(false); // Establecer como modo creación cuando se abre el modal para crear uno nuevo
      // Limpia los campos si es necesario
      setFormulario({
        nombreExternalCause: "",
        descripcionExternalCause: "",
        estatusExternalCause: "activo",
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedExternalCauseLocal(null); // Limpia el ExternalCause seleccionado
    setIsEditing(false); // Resetear el modo edición cuando se cierra el modal
    setModalOpen(false);
  };

  const handleSearchTermChange = (e) => {
    const searchTerm = e.target.value;
    setSearchTerm(searchTerm);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Iniciar la carga
      try {
        const response = await medicGeekApi.get(
          `/externalCauses?search=${searchTerm}`,
          {
            params: {
              $or: [
                { causeNm: { $regex: searchTerm, $options: "i" } },
                { causeDesc: { $regex: searchTerm, $options: "i" } },
              ],
            },
          }
        );
        const sortedExternalCauses = response.data.sort(
          (a, b) => a.causeId - b.causeId
        );
        setExternalCause(sortedExternalCauses);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false); // Finalizar carga
      }
    };

    fetchData();
  }, [searchTerm]);

  const pageCount = Math.ceil(externalCause.length / externalCausePerPage);

  const handlePageChange = ({ selected }) => {
    setPageNumber(selected);
  };

  const displayedExternalCauses = externalCause
    .filter((externalCause) => {
      // Filtrar externalCauses basados en el término de búsqueda
      return (
        externalCause.causeNm
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        externalCause.causeDesc.toLowerCase().includes(searchTerm.toLowerCase())
      );
    })
    .slice(
      pageNumber * externalCausePerPage,
      (pageNumber + 1) * externalCausePerPage
    );

  const updateExternalCauseForm = () => {
    medicGeekApi
      .get("/externalCauses")
      .then((response) => {
        setExternalCause(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const guardarNuevoExternalCause = () => {
    const nuevoExternalCause = {
      causeNm: document.getElementById("nombreExternalCause").value,
      causeDesc: document.getElementById("descripcionExternalCause").value,
      estatus:
        document.getElementById("estatusExternalCause").value === "activo"
          ? "Activo"
          : "Inactivo",
    };
    medicGeekApi
      .post("/externalCauses", nuevoExternalCause)
      .then((response) => {
        showSuccessAlert();
        closeModal();
        updateExternalCauseForm();
      })
      .catch((error) => {
        console.error("Error al crear el ExternalCause", error);
      });
  };

  const editarExternalCause = () => {
    if (selectedExternalCauseLocal && selectedExternalCauseLocal.causeId) {
      const { causeId } = selectedExternalCauseLocal;
      const nombreExternalCause = document.getElementById(
        "nombreExternalCause"
      ).value;
      const descripcionExternalCause = document.getElementById(
        "descripcionExternalCause"
      ).value;
      const estatusExternalCause =
        document.getElementById("estatusExternalCause").value === "activo"
          ? "Activo"
          : "Inactivo";

      const updatedExternalCause = {
        causeId, // Asegurarse de enviar el ID
        causeNm: nombreExternalCause,
        causeDesc: descripcionExternalCause,
        estatus: estatusExternalCause,
      };

      medicGeekApi
        .patch(`/externalCauses/${causeId}`, updatedExternalCause)
        .then((response) => {
          showSuccessEditAlert();
          closeModal();
          updateExternalCauseForm();
        })
        .catch((error) => {
          console.error("Error al actualizar el ExternalCause", error);
        });
    } else {
      // Manejo de error o mensaje de que no se puede actualizar
      showErrorAlert();
      console.error("No se puede actualizar: ExternalCause no válido.");
    }
  };

  const SkeletonTableRow = () => (
    <tr>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "60px" }}></div>
      </td>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "150px" }}></div>
      </td>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "100px" }}></div>
      </td>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "70px" }}></div>
      </td>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "200px" }}></div>
      </td>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "100px" }}></div>
      </td>
      <td>
        <div className="skeleton-placeholder" style={{ backgroundColor: "#e0e0e0", height: "20px", width: "60px" }}></div>
      </td>
    </tr>
  );

  return (
  <div className="pro-container">

    {/* ===================================================== */}
    {/* HEADER */}
    {/* ===================================================== */}

    <div className="pro-header">

      <div className="pro-header-left">

        <h2 className="pro-title">
          Causa Externa
        </h2>

        <p className="pro-subtitle">
          Gestión y mantenimiento de causas externas
        </p>

      </div>

      <div className="pro-actions">

        {/* SEARCH */}
        <div className="pro-search">

          <i className="bi bi-search"></i>

          <input
            type="search"
            placeholder="Buscar causa externa..."
            value={searchTerm}
            onChange={handleSearchTermChange}
          />

        </div>

        {/* BUTTON */}
        <button
          type="button"
          className="pro-btn-primary"
          title="Crear Nueva Causa Externa"
          onClick={() => openModal(null)}
        >
          <i className="bi bi-file-plus"></i>

          Nueva Causa Externa
        </button>

      </div>

    </div>

    {/* ===================================================== */}
    {/* TABLE DESKTOP */}
    {/* ===================================================== */}

    <div className="pro-card d-none d-md-block">

      <div className="table-responsive">

        <table className="table pro-table align-middle">

          <thead>

            <tr>

              <th style={{ width: "8%" }}>
                ID
              </th>

              <th style={{ width: "25%" }}>
                Nombre
              </th>

              <th style={{ width: "40%" }}>
                Descripción
              </th>

              <th style={{ width: "12%" }}>
                Estatus
              </th>

              <th
                className="text-center"
                style={{ width: "15%" }}
              >
                Acciones
              </th>

            </tr>

          </thead>

          <tbody>

            {loading ? (

              Array.from({ length: 15 }).map((_, index) => (

                <tr key={index}>

                  <td>
                    <div className="skeleton-line"></div>
                  </td>

                  <td>
                    <div className="skeleton-line"></div>
                  </td>

                  <td>
                    <div className="skeleton-line"></div>
                  </td>

                  <td>
                    <div className="skeleton-line"></div>
                  </td>

                  <td>
                    <div className="skeleton-line"></div>
                  </td>

                </tr>

              ))

            ) : (

              displayedExternalCauses.map((externalCause) => (

                <tr key={externalCause.causeId}>

                  {/* ID */}
                  <td>

                    <span className="expediente-badge">
                      #{externalCause.causeId}
                    </span>

                  </td>

                  {/* NAME */}
                  <td>

                    <div className="fw-semibold">

                      {externalCause.causeNm}

                    </div>

                  </td>

                  {/* DESCRIPTION */}
                  <td
                    className="truncate-text"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={externalCause.causeDesc}
                  >

                    {externalCause.causeDesc?.length > 45
                      ? `${externalCause.causeDesc.slice(0, 45)}...`
                      : externalCause.causeDesc}

                  </td>

                  {/* STATUS */}
                  <td>

                    <span
                      className={`gender-badge ${
                        externalCause.estatus === "Activo"
                          ? ""
                          : "opacity-75"
                      }`}
                    >
                      {externalCause.estatus}
                    </span>

                  </td>

                  {/* ACTIONS */}
                  <td>

                    <div className="pro-table-actions">

                      {/* EDIT */}
                      <button
                        type="button"
                        className="pro-action-btn warning"
                        title="Editar"
                        onClick={() => openModal(externalCause)}
                      >
                        <i className="bi bi-pencil-square"></i>
                      </button>

                      {/* DELETE */}
                      <button
                        type="button"
                        className="pro-action-btn info"
                        title="Eliminar"
                      >
                        <i className="bi bi-trash"></i>
                      </button>

                    </div>

                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

    </div>

    {/* ===================================================== */}
    {/* MOBILE */}
    {/* ===================================================== */}

    <div className="d-md-none">

      <div className="row g-3">

        {loading ? (

          Array.from({ length: 5 }).map((_, index) => (

            <div
              className="col-12"
              key={index}
            >

              <div className="mobile-patient-card">

                <div className="skeleton-line mb-3"></div>
                <div className="skeleton-line mb-3"></div>
                <div className="skeleton-line mb-3"></div>
                <div className="skeleton-line"></div>

              </div>

            </div>

          ))

        ) : (

          displayedExternalCauses.map((externalCause) => (

            <div
              className="col-12"
              key={externalCause.causeId}
            >

              <div className="mobile-patient-card">

                {/* TOP */}
                <div className="mobile-card-top">

                  <div className="patient-info">

                    <div className="patient-avatar">

                      {externalCause.causeNm?.charAt(0)}

                    </div>

                    <div>

                      <div className="patient-name">

                        {externalCause.causeNm}

                      </div>

                      <div className="patient-id">

                        ID #{externalCause.causeId}

                      </div>

                    </div>

                  </div>

                </div>

                {/* CONTENT */}
                <div className="mobile-card-content">

                  <p>

                    <strong>Descripción:</strong><br />

                    {externalCause.causeDesc}

                  </p>

                  <p>

                    <strong>Estatus:</strong><br />

                    <span className="gender-badge">

                      {externalCause.estatus}

                    </span>

                  </p>

                </div>

                {/* ACTIONS */}
                <div className="mobile-card-actions">

                  <button
                    type="button"
                    className="pro-action-btn warning"
                    onClick={() => openModal(externalCause)}
                  >
                    <i className="bi bi-pencil-square"></i>
                  </button>

                  <button
                    type="button"
                    className="pro-action-btn info"
                  >
                    <i className="bi bi-trash"></i>
                  </button>

                </div>

              </div>

            </div>

          ))

        )}

      </div>

    </div>

    {/* ===================================================== */}
    {/* PAGINATION */}
    {/* ===================================================== */}

    {!showForm && (

      <div className="pro-pagination">

        <nav>

          <ul className="pagination modern-pagination">

            {/* PREVIOUS */}
            <li
              className={`page-item ${
                pageNumber === 0
                  ? "disabled"
                  : ""
              }`}
            >

              <button
                className="page-link"
                onClick={() =>
                  handlePageChange({
                    selected: pageNumber - 1,
                  })
                }
              >
                <i className="bi bi-chevron-left"></i>
              </button>

            </li>

            {/* NUMBERS */}
            {Array.from(
              {
                length: Math.min(pageCount, 10),
              },
              (_, index) => (

                <li
                  key={index}
                  className={`page-item ${
                    pageNumber === index
                      ? "active"
                      : ""
                  }`}
                >

                  <button
                    className="page-link"
                    onClick={() =>
                      handlePageChange({
                        selected: index,
                      })
                    }
                  >
                    {index + 1}
                  </button>

                </li>

              )
            )}

            {/* NEXT */}
            <li
              className={`page-item ${
                pageNumber === pageCount - 1
                  ? "disabled"
                  : ""
              }`}
            >

              <button
                className="page-link"
                onClick={() =>
                  handlePageChange({
                    selected: pageNumber + 1,
                  })
                }
              >
                <i className="bi bi-chevron-right"></i>
              </button>

            </li>

          </ul>

        </nav>

      </div>

    )}

    {/* ===================================================== */}
    {/* MODAL */}
    {/* ===================================================== */}

    <Modal
      isOpen={modalOpen}
      toggle={closeModal}
      centered
    >

      <ModalHeader toggle={closeModal}>

        {isEditing
          ? "Editar Causa Externa"
          : "Crear Nueva Causa Externa"}

      </ModalHeader>

      <ModalBody>

        <Form className="pro-form">

          {/* NAME */}
          <FormGroup floating className="mb-4">

            <Input
              type="text"
              id="nombreExternalCause"
              className="pro-input"
              placeholder="Nombre"
              value={formulario.nombreExternalCause}
              onChange={(e) =>
                setFormulario({
                  ...formulario,
                  nombreExternalCause: e.target.value,
                })
              }
            />

            <Label for="nombreExternalCause">
              Nombre Causa Externa
            </Label>

          </FormGroup>

          {/* DESCRIPTION */}
          <FormGroup floating className="mb-4">

            <Input
              type="textarea"
              id="descripcionExternalCause"
              className="pro-input"
              placeholder="Descripción"
              value={formulario.descripcionExternalCause}
              onChange={(e) =>
                setFormulario({
                  ...formulario,
                  descripcionExternalCause: e.target.value,
                })
              }
            />

            <Label for="descripcionExternalCause">
              Descripción
            </Label>

          </FormGroup>

          {/* STATUS */}
          <FormGroup floating>

            <Input
              type="select"
              id="estatusExternalCause"
              className="pro-input"
              value={formulario.estatusExternalCause}
              onChange={(e) =>
                setFormulario({
                  ...formulario,
                  estatusExternalCause: e.target.value,
                })
              }
            >

              <option value="activo">
                Activo
              </option>

              <option value="inactivo">
                Inactivo
              </option>

            </Input>

            <Label for="estatusExternalCause">
              Estado
            </Label>

          </FormGroup>

        </Form>

      </ModalBody>

      <ModalFooter className="border-0 pt-0">

        <Button
          className="pro-btn-cancel"
          onClick={closeModal}
        >
          Cancelar
        </Button>

        <Button
          className="pro-btn-save"
          onClick={
            isEditing
              ? editarExternalCause
              : guardarNuevoExternalCause
          }
        >
          {isEditing
            ? "Guardar Edición"
            : "Guardar"}
        </Button>

      </ModalFooter>

    </Modal>

  </div>
);
};
export default ExternalCauseForm;
